import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { RoundedGreyBox, IconList, Button, LinkButton } from '@rentecarlo/component-library';
import { Title } from '@organisms/modals/TextModal/styles';
import { useComponentId, ComponentType, useAppSelector, useOptimizelyTrack } from '@hooks';
import analyticsClient from '@utils/analytics';
import { saveAndGoTo } from '@redux/reducer/quote/save.actions';
import { setProduct } from '@redux/reducer/quote/product.actions';
import { RootState } from '@redux/reducer';
import TickIcon from '@assets/black-tick.svg';
import BlueArrow from '@assets/blue-arrow.svg';
import QuoteStep from '@templates/QuoteStep';
import SeamlessPriceDisplay from '@molecules/blocks/SeamlessPriceDisplay';
import { getAge } from '@services/date';
import { logLTNAccepted } from '@services/ecommerceTracking';

const TitleContainer = styled.div`
  display: flex;
  margin-top: 30px;
`;

const InfoText = styled.div`
  font-family: ${({ theme }) => theme.standard};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 14px;
  line-height: 24px;
`;

const Text = styled.p`
  font-family: ${({ theme }) => theme.standard};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.08px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin-top: 32px;
`;

type IconProps = {
  isOpen: boolean;
};

const arrowIconStyles = (isOpen: boolean) => {
  if (isOpen) {
    return css`
      transform: rotate(180deg);
      margin-left: 16px;
    `;
  }
  return css`
    margin-left: 16px;
  `;
};

export const Icon = styled.img.attrs<IconProps>(() => {
  return {
    src: BlueArrow,
    alt: 'Click for more info',
  };
})<IconProps>`
  ${({ isOpen }) => arrowIconStyles(isOpen)}
`;

const SpecialTitle: React.FC = () => {
  const creator = useComponentId();
  return (
    <TitleContainer>
      <Title id={creator(ComponentType.TEXT, 'specialTitle')}>
        You're eligible for our Learner + New Driver cover!
      </Title>
    </TitleContainer>
  );
};

const SeamlessField: React.FC = () => {
  const [showPriceAfterPass, setShowPriceAfterPass] = useState(false);
  const [howItWorks, setHowItWorks] = useState(false);

  const handleToggle = () => {
    setShowPriceAfterPass(!showPriceAfterPass);
    analyticsClient.trackEvent('web_seamless_product_choice_show_price_after_pass');
  };

  const dispatch = useDispatch();
  const creator = useComponentId();
  const quoteUuid = useAppSelector((state) => state.quote.save.uuid);
  const quotePrice = useAppSelector((state) => state.quote.save.price);
  const isFastRepurchase = useSelector((state: RootState) => state.quote.save.isFastRepurchase);
  const productType = useSelector((state: RootState) => state.quote.product.productType);
  const altProduct = useSelector((state: RootState) => state.quote.alternativeProducts);
  const altProductPrice = (altProduct.seamless?.price as number).toFixed(2).toString();
  const driverAge = useAppSelector((state) => state.quote.driver.birthdate);
  const sendEvent = useOptimizelyTrack();

  const handleSeamlessOptIn = () => {
    dispatch(setProduct('seamless'));
    if (isFastRepurchase) {
      dispatch(saveAndGoTo('final-quote', true));
    } else {
      dispatch(saveAndGoTo('important-information', true));
    }
    analyticsClient.trackEvent('web_seamless_product_choice_yes', {
      seamless_pass_price: altProductPrice,
    });
    analyticsClient.trackUserProperty('driverAge', getAge(driverAge));
    logLTNAccepted(true, quoteUuid);
    sendEvent('learner_to_newdriver_opt_in');
  };

  const handleSeamlessOptOut = () => {
    if (productType === 'seamless') {
      dispatch(setProduct('ldp'));
    }
    analyticsClient.trackEvent('web_seamless_product_choice_no', {
      seamless_pass_price: altProductPrice,
    });
    analyticsClient.trackUserProperty('driverAge', getAge(driverAge));
    logLTNAccepted(false, quoteUuid);
    sendEvent('learner_to_newdriver_opt_out');
    if (isFastRepurchase) {
      dispatch(saveAndGoTo('final-quote'));
    } else {
      dispatch(saveAndGoTo('important-information'));
    }
  };

  return (
    <QuoteStep paddingTop='0' titleMarginBottom='0' id={creator(ComponentType.BUTTON, 'continue')}>
      <Container id='productChoice-component-altSeamlessOffer'>
        <SpecialTitle />
        <RoundedGreyBox padding={[16, 16, 16, 16]}>
          <InfoText id='productChoice-text-altSeamlessInfo'>
            We've created a new policy that covers you while you're learning <b>AND</b> after you
            pass your test. Easy!
          </InfoText>
        </RoundedGreyBox>
        <SeamlessPriceDisplay
          leftActive={!showPriceAfterPass}
          handleToggle={handleToggle}
          learnerPrice={quotePrice}
          priceAfterPass={altProductPrice}
        />
        <LinkButton
          bold
          id='web-productChoice-altSeamless-howItWorks'
          large
          action={() => setHowItWorks(!howItWorks)}
        >
          <div
            id='productChoice-text-altSeamlessHowItWorks'
            style={{ textDecorationLine: 'underline' }}
          >
            Here's how it works
            <Icon
              src={BlueArrow}
              alt='blue arrow dropdown'
              isOpen={howItWorks}
              id='productChoice-component-howItWorksDropdown'
            />
          </div>
        </LinkButton>
        {howItWorks && (
          <IconList
            id='productChoice-component-altSeamlessInfoList'
            icon={TickIcon}
            items={[
              <Text id='productChoice-text-altSeamlessInfoListOne'>
                <b>One monthly subscription</b> covers you to drive while learning, during your test
                <b> and </b>
                after you pass.
              </Text>,
              <Text id='productChoice-text-altSeamlessInfoListOne'>
                When you pass, you'll roll seamlessly onto our New Driver cover.
                <b> We'll discount your first month after passing,</b> so it matches your learner
                price.
              </Text>,
              <Text id='productChoice-text-altSeamlessInfoListTwo'>
                Download the <b>Veygo app,</b> monitor your driving and <b>save up to 30%</b> in the
                first year after passing your test.
              </Text>,
              <Text id='productChoice-text-altSeamlessInfoListThree' style={{ fontWeight: 'bold' }}>
                Cancel anytime, fee free.
              </Text>,
            ]}
          />
        )}
      </Container>
      <ButtonContainer>
        <Button
          id={creator(ComponentType.BUTTON, 'seamlessAltOfferContinueWithSeamless')}
          onClick={() => handleSeamlessOptIn()}
          data-amp-unmask
        >
          Yes, please
        </Button>
        <Button
          id={creator(ComponentType.BUTTON, 'seamlessAltOfferNoThanks')}
          onClick={() => handleSeamlessOptOut()}
          backgroundColor='transparent'
          borderColor='transparent'
          textColor='#171717'
          textHoverColor='#fff'
          data-amp-unmask
        >
          Just Learner cover, thanks
        </Button>
      </ButtonContainer>
    </QuoteStep>
  );
};

export default SeamlessField;
